import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Filter, X } from "lucide-react";

export default function Catalog() {
    const { categoria } = useParams();
    const [params, setParams] = useSearchParams();
    const search = params.get("q") || "";
    const printerParam = params.get("printer") || "";
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 24;
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brandFilter, setBrandFilter] = useState(params.get("brand") || "");
    const [printerInput, setPrinterInput] = useState(printerParam);
    const [printerFilter, setPrinterFilter] = useState(printerParam);
    const [printerSuggestions, setPrinterSuggestions] = useState([]);
    const [showSugg, setShowSugg] = useState(false);
    const [inStockOnly, setInStockOnly] = useState(false);
    const [sort, setSort] = useState("default");
    const [openFilters, setOpenFilters] = useState(false);

    useEffect(() => {
        api.get("/products/brands").then((r) => setBrands(r.data.brands || []));
        api.get("/categories").then((r) => setCategories(r.data));
    }, []);

    // reset page when filters change
    useEffect(() => { setPage(1); }, [categoria, brandFilter, search, printerFilter, inStockOnly]);

    useEffect(() => {
        const q = new URLSearchParams();
        if (categoria) q.set("category", categoria);
        if (brandFilter) q.set("brand", brandFilter);
        if (search) q.set("search", search);
        if (printerFilter) q.set("printer", printerFilter);
        if (inStockOnly) q.set("in_stock", "true");
        const qList = new URLSearchParams(q);
        qList.set("limit", PAGE_SIZE);
        qList.set("skip", (page - 1) * PAGE_SIZE);
        api.get(`/products?${qList.toString()}`).then((r) => setProducts(r.data));
        api.get(`/products/count?${q.toString()}`).then((r) => setTotal(r.data.total));
    }, [categoria, brandFilter, search, printerFilter, inStockOnly, page]);

    // printer autocomplete
    useEffect(() => {
        if (!printerInput || printerInput.length < 2) {
            setPrinterSuggestions([]);
            return;
        }
        const t = setTimeout(() => {
            api.get(`/printers/suggest?q=${encodeURIComponent(printerInput)}`)
                .then((r) => setPrinterSuggestions(r.data));
        }, 300);
        return () => clearTimeout(t);
    }, [printerInput]);

    const applyPrinter = (model) => {
        setPrinterFilter(model);
        setPrinterInput(model);
        setShowSugg(false);
        const next = new URLSearchParams(params);
        if (model) next.set("printer", model);
        else next.delete("printer");
        setParams(next);
    };
    const clearPrinter = () => applyPrinter("");

    const sorted = useMemo(() => {
        const list = [...products];
        if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
        if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
        if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
        return list;
    }, [products, sort]);

    const currentCat = categories.find((c) => c.slug === categoria);

    return (
        <div data-testid="catalog-page">
            <section className="border-b-2 border-ink bg-white">
                <div className="container-edge py-10">
                    <div className="label-mono text-magenta mb-3">Catálogo</div>
                    <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
                        {currentCat ? currentCat.name : search ? `Resultados: "${search}"` : "Todos os produtos"}
                    </h1>
                    {currentCat?.description && (
                        <p className="mt-4 max-w-2xl text-ink/70">{currentCat.description}</p>
                    )}
                </div>
            </section>

            <section className="container-edge py-10">
                <div className="grid lg:grid-cols-[260px_1fr] gap-8">
                    {/* Sidebar */}
                    <aside
                        className={`${openFilters ? "block fixed inset-0 z-50 bg-paper p-6 overflow-y-auto" : "hidden"} lg:block lg:static lg:p-0`}
                        data-testid="filters-sidebar"
                    >
                        <div className="lg:hidden flex justify-between items-center mb-6">
                            <h3 className="font-display font-black text-xl">Filtros</h3>
                            <button
                                onClick={() => setOpenFilters(false)}
                                className="border-2 border-ink p-2"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="border-2 border-ink p-5 bg-white space-y-6">
                            <div>
                                <div className="label-mono mb-3 inline-flex items-center gap-1">
                                    <span className="text-magenta">●</span> Modelo de impressora
                                </div>
                                <div className="relative">
                                    <input
                                        value={printerInput}
                                        onChange={(e) => { setPrinterInput(e.target.value); setShowSugg(true); }}
                                        onFocus={() => setShowSugg(true)}
                                        onBlur={() => setTimeout(() => setShowSugg(false), 150)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPrinter(printerInput); } }}
                                        placeholder="ex: LaserJet 1010, LBP-2900…"
                                        className="input-edge text-xs pr-7"
                                        data-testid="printer-search-input"
                                    />
                                    {printerInput && (
                                        <button onClick={clearPrinter} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink/50 hover:text-magenta" data-testid="printer-clear">
                                            ✕
                                        </button>
                                    )}
                                    {showSugg && printerSuggestions.length > 0 && (
                                        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border-2 border-ink shadow-[4px_4px_0_0_#0A0A0A] max-h-72 overflow-y-auto">
                                            {printerSuggestions.map((s) => (
                                                <button
                                                    key={s.model}
                                                    type="button"
                                                    onMouseDown={() => applyPrinter(s.model)}
                                                    className="block w-full text-left px-3 py-2 text-xs hover:bg-magenta hover:text-white border-b border-ink/10 last:border-b-0"
                                                    data-testid={`printer-suggestion-${s.model}`}
                                                >
                                                    <span className="font-mono">{s.model}</span>
                                                    <span className="ml-2 text-ink/40">({s.count})</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {printerFilter && (
                                    <div className="mt-2 inline-flex items-center gap-1 bg-magenta text-white px-2 py-1 text-xs font-mono">
                                        Filtro: {printerFilter}
                                        <button onClick={clearPrinter} className="ml-1 hover:text-paper">✕</button>
                                    </div>
                                )}
                            </div>

                            <div className="border-t-2 border-ink pt-5">
                                <div className="label-mono mb-3">Categorias</div>
                                <div className="space-y-1.5">
                                    <Link
                                        to="/loja"
                                        className={`block text-sm ${!categoria ? "text-magenta font-bold" : "hover:text-magenta"}`}
                                    >
                                        Todas
                                    </Link>
                                    {categories.map((c) => (
                                        <Link
                                            key={c.id}
                                            to={`/loja/${c.slug}`}
                                            className={`block text-sm ${categoria === c.slug ? "text-magenta font-bold" : "hover:text-magenta"}`}
                                        >
                                            {c.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                            <div className="border-t-2 border-ink pt-5">
                                <div className="label-mono mb-3">Marca</div>
                                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                                    <button
                                        className={`block text-sm ${!brandFilter ? "text-magenta font-bold" : "hover:text-magenta"}`}
                                        onClick={() => setBrandFilter("")}
                                    >
                                        Todas
                                    </button>
                                    {brands.map((b) => (
                                        <button
                                            key={b}
                                            className={`block text-sm ${brandFilter === b ? "text-magenta font-bold" : "hover:text-magenta"}`}
                                            onClick={() => setBrandFilter(b)}
                                            data-testid={`brand-filter-${b}`}
                                        >
                                            {b}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="border-t-2 border-ink pt-5">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={inStockOnly}
                                        onChange={(e) => setInStockOnly(e.target.checked)}
                                        className="w-4 h-4 accent-magenta"
                                    />
                                    <span className="text-sm font-bold uppercase tracking-wider">
                                        Apenas em stock
                                    </span>
                                </label>
                            </div>
                        </div>
                    </aside>

                    {/* Main */}
                    <div>
                        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                            <button
                                onClick={() => setOpenFilters(true)}
                                className="lg:hidden btn-secondary"
                            >
                                <Filter size={16} /> Filtros
                            </button>
                            <div className="font-mono text-sm text-ink/60" data-testid="results-count">
                                {total} produtos encontrados
                            </div>
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                className="input-edge w-auto py-2"
                                data-testid="sort-select"
                            >
                                <option value="default">Ordenar: Padrão</option>
                                <option value="price_asc">Preço: ↑</option>
                                <option value="price_desc">Preço: ↓</option>
                                <option value="name">Nome A-Z</option>
                            </select>
                        </div>

                        {sorted.length === 0 ? (
                            <div className="border-2 border-ink p-12 text-center bg-white">
                                <div className="font-display font-black text-2xl">
                                    Sem resultados
                                </div>
                                <p className="mt-2 text-ink/60">
                                    Tente ajustar os filtros ou pesquisar por outro termo.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                                    {sorted.map((p) => (
                                        <ProductCard key={p.id} product={p} />
                                    ))}
                                </div>

                                {/* Pagination */}
                                {total > PAGE_SIZE && (
                                    <div className="mt-10 flex items-center justify-between flex-wrap gap-4 border-t-2 border-ink pt-6" data-testid="pagination">
                                        <div className="font-mono text-xs text-ink/60">
                                            Página {page} de {Math.ceil(total / PAGE_SIZE)} · {total} produtos
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                                disabled={page === 1}
                                                className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                                                data-testid="prev-page-btn"
                                            >
                                                ← Anterior
                                            </button>
                                            {/* Numbered pages — show 5 around current */}
                                            {(() => {
                                                const totalPages = Math.ceil(total / PAGE_SIZE);
                                                const start = Math.max(1, page - 2);
                                                const end = Math.min(totalPages, start + 4);
                                                const pages = [];
                                                for (let i = start; i <= end; i++) pages.push(i);
                                                return pages.map((n) => (
                                                    <button
                                                        key={n}
                                                        onClick={() => setPage(n)}
                                                        className={`min-w-[40px] h-10 border-2 border-ink font-mono font-bold ${
                                                            n === page ? "bg-magenta text-white border-magenta" : "bg-white hover:bg-ink hover:text-white"
                                                        }`}
                                                        data-testid={`page-${n}`}
                                                    >
                                                        {n}
                                                    </button>
                                                ));
                                            })()}
                                            <button
                                                onClick={() => setPage((p) => Math.min(Math.ceil(total / PAGE_SIZE), p + 1))}
                                                disabled={page >= Math.ceil(total / PAGE_SIZE)}
                                                className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                                                data-testid="next-page-btn"
                                            >
                                                Seguinte →
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
